
const NULL_CTX_BRAND: unique symbol = Symbol( "null_canvas_context" );

let measure_ctx: CanvasRenderingContext2D | null = null;

export function getMeasureContext(): CanvasRenderingContext2D
{
  if ( !measure_ctx )
  {
    const canvas = document.createElement( "canvas" );
    canvas.width = 1;
    canvas.height = 1;
    measure_ctx = canvas.getContext( "2d" ) as CanvasRenderingContext2D;
  }
  return measure_ctx;
}

const NOOP = (): void => {};
const GRADIENT_STUB = { addColorStop: NOOP } as unknown as CanvasGradient;
const PATTERN_STUB = { setTransform: NOOP } as unknown as CanvasPattern;

const FORWARDED_TO_MEASURE = new Set<PropertyKey>( [ "measureText" ] );

export function createNullCanvasContext( source: HTMLCanvasElement | CanvasRenderingContext2D ): CanvasRenderingContext2D
{
  if ( isNullCanvasContext( source as CanvasRenderingContext2D ) )
    return source as CanvasRenderingContext2D;
  const canvas = source instanceof HTMLCanvasElement ? source : source.canvas;
  const mctx = getMeasureContext();
  const noop_cache = new Map<PropertyKey, () => void>();
  const forwarded_cache = new Map<PropertyKey, ( ...args: unknown[] ) => unknown>();

  const handler: ProxyHandler<Record<PropertyKey, unknown>> = {
    get( _target, prop )
    {
      if ( prop === NULL_CTX_BRAND ) return true;
      if ( prop === "canvas" ) return canvas;
      if ( prop === "font" ) return mctx.font;
      if ( prop === "textAlign" ) return mctx.textAlign;
      if ( prop === "textBaseline" ) return mctx.textBaseline;
      if ( prop === "letterSpacing" ) return ( mctx as unknown as { letterSpacing?: string } ).letterSpacing ?? "0px";

      if ( FORWARDED_TO_MEASURE.has( prop ) )
      {
        let fn = forwarded_cache.get( prop );
        if ( !fn )
        {
          const method = ( mctx as unknown as Record<PropertyKey, ( ...a: unknown[] ) => unknown> )[prop];
          fn = ( ...args: unknown[] ) => method.apply( mctx, args );
          forwarded_cache.set( prop, fn );
        }
        return fn;
      }

      switch ( prop )
      {
        case "createLinearGradient":
        case "createRadialGradient":
        case "createConicGradient":
          return () => GRADIENT_STUB;
        case "createPattern":
          return () => PATTERN_STUB;
        case "getTransform":
          return () => new DOMMatrix();
        case "isPointInPath":
        case "isPointInStroke":
          return () => false;
        case "getLineDash":
          return () => [];
        case "getImageData":
        case "createImageData":
          return () => new ImageData( 1, 1 );
        case "getContextAttributes":
          return () => ( { alpha: true, desynchronized: false, colorSpace: "srgb", willReadFrequently: false } );
      }

      const value = ( mctx as unknown as Record<PropertyKey, unknown> )[prop];
      if ( typeof value === "function" )
      {
        let fn = noop_cache.get( prop );
        if ( !fn )
        {
          fn = NOOP;
          noop_cache.set( prop, fn );
        }
        return fn;
      }
      return value;
    },
    set( _target, prop, value )
    {
      if ( prop === "font" ) mctx.font = value as string;
      else if ( prop === "textAlign" ) mctx.textAlign = value as CanvasTextAlign;
      else if ( prop === "textBaseline" ) mctx.textBaseline = value as CanvasTextBaseline;
      else if ( prop === "letterSpacing" ) ( mctx as unknown as { letterSpacing?: string } ).letterSpacing = value as string;
      return true;
    },
    has( _target, prop )
    {
      return prop === NULL_CTX_BRAND || prop in mctx;
    },
  };

  return new Proxy( {}, handler ) as unknown as CanvasRenderingContext2D;
}

export function isNullCanvasContext( ctx: CanvasRenderingContext2D | null | undefined ): boolean
{
  return !!ctx && ( ctx as unknown as Record<symbol, unknown> )[NULL_CTX_BRAND] === true;
}
